import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useMe } from "./context/MeContext";
import Home from "./pages/HomePage";
import GenericError from "./components/GenericError";
import Navigation from "./components/Navigation";
import React, { ReactNode } from "react";
import ExperienceListPage from "./pages/Experiences/ExperienceListPage";
import ExperienceDetailPage from "./pages/Experiences/ExperienceDetailPage";
import ExperiencesParentPage from "./pages/Experiences/ExperiencesParentPage";
import AdminUserListPage from "./pages/Admin/AdminUserListPage";
import AdminCompanyListPage from "./pages/Admin/AdminCompanyListPage";
import AdminExperienceListPage from "./pages/Admin/AdminExperienceListPage";
import SdkPage from "./pages/SdkPage";
import AdminPage from "./pages/AdminPage";
import AdminHomePage from "./pages/Admin/AdminHomePage";
import AdminExperienceDetailPage from "./pages/Admin/AdminExperienceDetailPage";
import AdminCompanyDetailPage from "./pages/Admin/AdminCompanyDetailPage";
import AdminUserDetailPage from "./pages/Admin/AdminUserDetailPage";
import SupportPage from "./pages/SupportPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import AccountPage from "./pages/AccountPage";
import DocumentationPage from "./pages/DocumentationPage";
import ProfileCompanyPage from "./pages/Profile/ProfileCompanyPage";
import VideoPage from "./pages/VideoPage";
import RecordingPage from "./pages/RecordingPage";
import AdminVideoPage from "./pages/Admin/AdminVideoPage";
import AdminAssetsListPage from "./pages/Admin/AdminAssetsListPage";
import ExperienceAnalyticsPage from "./pages/Analytics/ExperienceAnalyticsPage";
import AdminTitlesListPage from "./pages/Admin/AdminTitlesListPage";
import AdminTitlesDetailPage from "./pages/Admin/AdminTitlesDetailPage";
import AdminSessionsDetailPage from "./pages/Admin/AdminSessionsDetailPage";
import AdminSessionsHomePage from "./pages/Admin/AdminSessionsHomePage";
import AdminLiveAppSessionsListPage from "./pages/Admin/AdminLiveAppSessionsListPage";
import AdminSessionsLiveAppDetailPage from "./pages/Admin/AdminSessionsLiveAppDetailPage";
import AdminSessionsListPage from "./pages/Admin/AdminSessionsListPage";
import AdminLiveNowPage from "./pages/Admin/AdminLiveNowPage";
import AdminStatusPage from "./pages/Admin/AdminStatusPage";
import AdminFeaturePromotionsListPage from "./pages/Admin/AdminFeaturePromotionsListPage";
import AdminFeaturePromotionsDetailPage from "./pages/Admin/AdminFeaturePromotionsDetailPage";
import AdminArticlesListPage from "./pages/Admin/AdminArticlesListPage";
import AdminArticlesDetailPage from "./pages/Admin/AdminArticlesDetailPage";
import AdminAnalyticsStreamsSummaryPage from "./pages/Admin/Analytics/AdminAnalyticsStreamsSummaryPage";
import AdminAnalyticsUsersSummaryPage from "./pages/Admin/Analytics/AdminAnalyticsUsersSummaryPage";
import AdminAnalyticsRankingsPage from "./pages/Admin/Analytics/AdminAnalyticsRankingsPage";
import AdminAnalyticsTitlesPage from "./pages/Admin/Analytics/AdminAnalyticsTitlesPage";
import AdminAnalyticsOverviewPage from "./pages/Admin/Analytics/AdminAnalyticsOverviewPage";
import AdminExperienceAnalyticsSummaryPage from "./pages/Admin/Analytics/AdminExperienceAnalyticsSummaryPage";
import AdminAnalyticsSubscriptionsSummaryPage from "./pages/Admin/Analytics/AdminAnalyticsSubscriptionsSummaryPage";
import AdminAnalyticsEnterpriseStreamsPage from "./pages/Admin/Analytics/AdminAnalyticsEnterpriseStreamsPage";
import AdminAnalyticsEnterpriseStreamDetailPage from "./pages/Admin/Analytics/AdminAnalyticsEnterpriseStreamDetailPage";
import AdminCampaignsListPage from "./pages/Admin/AdminCampaignsListPage";
import AdminCampaignsDetailPage from "./pages/Admin/AdminCampaignsDetailPage";
import AdminCampaignRewardDetailPage from "./pages/Admin/AdminCampaignRewardDetailPage";
import AdminAdvertsListPage from "./pages/Admin/AdminAdvertsListPage";
import AdminAdvertsDetailPage from "./pages/Admin/AdminAdvertsDetailPage";

// Define the type for Layout props
interface LayoutProps {
  children?: ReactNode;
}

// Layout component to wrap pages with Navigation
const Layout: React.FC<LayoutProps> = () => (
  <div className="grid grid-cols-12 2xl:grid-cols-9 sm:divide-x sm:divide-x-1 divide-solid divide-stone-900 sm:h-screen overflow-x-hidden">
    <div className="col-span-12 sm:col-span-4 xl:col-span-2 2xl:col-span-1">
      <Navigation />
    </div>
    <div className="col-span-12 sm:col-span-8 xl:col-span-10 2xl:col-span-8 h-screen overflow-y-hidden overflow-x-hidden">
      <div className="py-4 px-4 sm:px-8 md:px-16 overflow-x-hidden overflow-y-auto h-[calc(100%-80px)] sm:h-full">
        <Outlet />
        <span className="block h-20 sm:hidden" />
      </div>
    </div>
  </div>
);

export default function RoutedApp() {
  const { me, isLoading: meLoading } = useMe();

  if (!me) {
    return (
      <GenericError errorMessage="There was a problem with your user account." />
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route path="" element={<Home />} />
          <Route path="experiences" element={<ExperiencesParentPage />}>
            <Route path="" element={<ExperienceListPage />} />
            <Route path=":companyId" element={<Outlet />}>
              <Route path=":experienceId" element={<ExperienceDetailPage />} />
            </Route>
          </Route>
          <Route path="videos" element={<VideoPage />} />
          <Route
            path="companies/:companyId/experiences/:experienceId/recordings/:recordingId"
            element={<RecordingPage />}
          />
          <Route path="sdk" element={<SdkPage />} />
          <Route path="admin" element={<AdminPage />}>
            <Route path="" element={<AdminHomePage />} />
            <Route path="articles" element={<AdminArticlesListPage />} />
            <Route path="articles/:articleId" element={<AdminArticlesDetailPage />} />
            <Route path="live" element={<AdminLiveNowPage />} />
            <Route path="users" element={<AdminUserListPage />} />
            <Route path="users/:userId" element={<AdminUserDetailPage />} />
            <Route path="companies" element={<AdminCompanyListPage />} />
            <Route path="status" element={<AdminStatusPage />} />
            <Route path="featurepromotions" element={<AdminFeaturePromotionsListPage />} />
            <Route path="featurepromotions/:promotionId" element={<AdminFeaturePromotionsDetailPage />} />
            <Route path="adverts" element={<AdminAdvertsListPage />} />
            <Route path="adverts/:advertId" element={<AdminAdvertsDetailPage />} />
            <Route path="campaigns" element={<AdminCampaignsListPage />} />
            <Route path="campaigns/:campaignId" element={<AdminCampaignsDetailPage />} />
            <Route path="campaigns/:campaignId/rewards/:rewardId" element={<AdminCampaignRewardDetailPage />} />
            <Route
              path="companies/:companyId"
              element={<AdminCompanyDetailPage />}
            />
            <Route path="experiences" element={<AdminExperienceListPage />} />
            <Route
              path="experiences/:companyId/:experienceId"
              element={<AdminExperienceDetailPage />}
            />
            <Route path="videos" element={<AdminVideoPage />} />
            <Route path="assets" element={<AdminAssetsListPage />} />
            <Route path="titles" element={<AdminTitlesListPage />} />
            <Route path="titles/:titleId" element={<AdminTitlesDetailPage />} />
            <Route path="sessions" element={<AdminSessionsHomePage />}>
              <Route path="sdk" element={<AdminSessionsListPage />} />
              <Route path="liveapp" element={<AdminLiveAppSessionsListPage />} />            
            </Route>
            <Route path="sessions/sdk/:userId/:sessionId" element={<AdminSessionsDetailPage />} />
            <Route path="sessions/liveapp/:userId/:sessionId" element={<AdminSessionsLiveAppDetailPage />} />
          </Route>
          <Route path="analytics" element={<AnalyticsPage />}>
            <Route path="" element={<AdminAnalyticsOverviewPage />} />
            <Route path="streams" element={<AdminAnalyticsStreamsSummaryPage />} />
            <Route path="users" element={<AdminAnalyticsUsersSummaryPage />} />
            <Route path="rankings" element={<AdminAnalyticsRankingsPage />} />
            <Route path="titles" element={<AdminAnalyticsTitlesPage />} />
            <Route path="sdk-streams" element={<AdminExperienceAnalyticsSummaryPage />} />
            <Route path="subscriptions" element={<AdminAnalyticsSubscriptionsSummaryPage />} />
            <Route path="enterprise-streams" element={<AdminAnalyticsEnterpriseStreamsPage />} />
          </Route>
          <Route path="analytics/experience/:companyId/:experienceId" element={<ExperienceAnalyticsPage />} />
          <Route path="analytics/enterprise-streams/:experienceId" element={<AdminAnalyticsEnterpriseStreamDetailPage />} />
          <Route path="profile" element={<AccountPage />} />
          <Route
            path="profile/company/:companyId"
            element={<ProfileCompanyPage />}
          />
          <Route path="support" element={<SupportPage />} />
          <Route path="documentation" element={<DocumentationPage />} />
        </Route>

        {/* Catch-all route to redirect */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}
